import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { handleHashScroll, ScrollToTop } from './utils/scrollUtils';
import { trackPageView } from './firebase/config';
import i18n from './i18n/i18n';
import { localizePath, parseLocalePath } from './seo/site';
import Seo from './components/Seo/Seo';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Home from './pages/Home/Home';
import Services from './pages/Services/Services';
import Destinations from './pages/Destinations/Destinations';
import Cargo from './pages/Cargo/Cargo';
import WhyUs from './pages/WhyUs/WhyUs';
import Privacy from './pages/Privacy/Privacy';
import Terms from './pages/Terms/Terms';
import CarriageTerms from './pages/CarriageTerms/CarriageTerms';
import Relocations from './pages/Relocations/Relocations';
import NotFound from './pages/NotFound/NotFound';
import ConsentBanner from './components/ConsentBanner/ConsentBanner';
import { useConsent } from './hooks/useConsent';

const PAGE_COMPONENTS = { home: Home, services: Services, destinations: Destinations, cargo: Cargo, whyUs: WhyUs, privacy: Privacy, terms: Terms, carriageTerms: CarriageTerms, relocations: Relocations };

function PageEffects({ page }) {
  const location = useLocation();
  const { t } = useTranslation();
  useEffect(() => {
    handleHashScroll(location.hash);
    trackPageView(location.pathname, t(`seo.${page?.key || 'notFound'}.title`));
  }, [location, page, t]);
  return <ScrollToTop />;
}

function SiteLayout({ page, locale }) {
  const { shouldShowBanner, acceptAll, rejectAll } = useConsent({ gtmId: 'GTM-T7L7KF37', gaMeasurementId: 'G-E2PBTY3VH2' });
  const Page = PAGE_COMPONENTS[page?.key] || NotFound;
  return (
    <div className="App">
      <Seo page={page} locale={locale} />
      <PageEffects page={page} />
      <Header />
      <main><Page /></main>
      <Footer />
      <ConsentBanner visible={shouldShowBanner} onAcceptAll={acceptAll} onRejectAll={rejectAll} />
    </div>
  );
}

// The browser and HTML generator render the same components in the URL's language.
export function AppContent() {
  const location = useLocation();
  const { locale, pagePath, page } = parseLocalePath(location.pathname);
  const localeI18n = useMemo(() => i18n.cloneInstance({ lng: locale, initImmediate: false }), [locale]);
  const canonicalPath = localizePath(pagePath, locale);
  return (
    <I18nextProvider i18n={localeI18n}>
      {page && location.pathname !== canonicalPath
        ? <Navigate replace to={`${canonicalPath}${location.search}${location.hash}`} />
        : <SiteLayout page={page} locale={locale} />}
    </I18nextProvider>
  );
}

export default function App() {
  return <BrowserRouter><AppContent /></BrowserRouter>;
}
